import { useEffect, useState } from 'react';
import { useUpdateEffect } from '@/shared/lib/customHooks';
import type { EventHrInfoProps } from './index';

/**
 * Cycles through the events that share one heart-rate point so the strip header can show them one at a time. Keeps the highlighted position in local state, resolves the current event and its ECG channel with a fallback to the AI channel, pushes the matching index up to the chart while the tab is active and more than one event is stacked, and rewinds to the first event whenever the event list changes.
 *
 * @param props - Header props carrying eventIndexes, events, isActive, aiChannel and setSelectedEventIndex.
 * @returns The header view model with eventIndex, the current event, its channel and the left and right navigation handlers.
 */
const useEventHrInfo = ({
  eventIndexes,
  events,
  isActive,
  aiChannel,
  setSelectedEventIndex,
}: Pick<
  EventHrInfoProps,
  'eventIndexes' | 'events' | 'isActive' | 'aiChannel' | 'setSelectedEventIndex'
>) => {
  const [eventIndex, setEventIndex] = useState(0);

  const event = events[eventIndex] || events[0];
  const channel = event?.channel || aiChannel;

  //* if is select event 1, click arrow left will select event 3, arrow right will select event 2
  /**
   * Steps back one event in the stack of events sharing the heart-rate point. Decrements the highlighted index and wraps around to the last event when it would go below zero.
   *
   * @returns Nothing.
   */
  const onClickLeft = () => {
    const newIndex = eventIndex - 1;
    setEventIndex(newIndex < 0 ? (events?.length ?? 0) - 1 : newIndex);
  };

  /**
   * Steps forward one event in the stack of events sharing the heart-rate point. Increments the highlighted index and wraps back to the first event when it would pass the end of the list.
   *
   * @returns Nothing.
   */
  const onClickRight = () => {
    const newIndex = eventIndex + 1;
    setEventIndex(newIndex > (events?.length ?? 0) - 1 ? 0 : newIndex);
  };

  useEffect(() => {
    if (isActive && events?.length > 1) {
      setSelectedEventIndex(eventIndexes[eventIndex]);
    }
  }, [eventIndexes, eventIndex, isActive]);

  useUpdateEffect(() => {
    setEventIndex(0);
  }, [events]);

  return {
    eventIndex,
    event,
    channel,
    onClickLeft,
    onClickRight,
  };
};

export default useEventHrInfo;
